import React, { useCallback, useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Box, CircularProgress } from '@mui/material';
import config from './amplifyconfiguration.json';
import './App.css';
import { MainContainer } from 'app/layout/MainContainer';
import { LoginScreen } from 'features/auth/components/LoginScreen';

Amplify.configure(config);

export function App() {
  return <MainContainer />;
}

const isE2E = process.env.REACT_APP_E2E === 'true';
const E2EApp: React.FC = () => <App />;

// E2E バイパス中でもログイン UI を強制レンダリングするためのクエリパラメータ。
// 通常 E2E (`REACT_APP_E2E=true`) は認証経路をスキップするが、
// auth-email-mfa.spec.ts などログイン UI 自体を検証するテストでは `?e2eAuthTest=1` を付与する。
const shouldForceAuthUI = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('e2eAuthTest') === '1';
  } catch {
    return false;
  }
};

const AuthenticatedApp: React.FC = () => {
  // null = 認証状態確認中、true = 認証済み、false = 未認証
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAuthSession()
      .then((session) => {
        if (cancelled) return;
        setAuthed(!!session?.tokens?.idToken);
      })
      .catch(() => {
        if (cancelled) return;
        setAuthed(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAuthSuccess = useCallback(() => {
    setAuthed(true);
  }, []);

  if (authed === null) {
    return (
      <Box
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!authed) {
    return <LoginScreen onSuccess={handleAuthSuccess} />;
  }

  return <App />;
};

const Root: React.FC = () => {
  if (isE2E && !shouldForceAuthUI()) {
    return <E2EApp />;
  }
  return <AuthenticatedApp />;
};

export default Root;
